import {
  DTOValueFloat,
  DTOValueInt,
  DTOValueSelect,
  NextSurveyItemTypeHint,
  QuestionnaireAnswer,
  QuestionnaireCategoryItemsItemOneOf,
  QuestionnaireCategoryItemsItemOneOfTwo,
  QuestionnaireResponseNextQuestionResponse,
  QuestionnaireResponseNextQuestionResponseNextQuestionAnyOf,
  QuestionnaireResponseStatus,
  UpdateQuestionnaireResponseAnswerInput,
} from '@/api/generated-catch-backend/model';
import { getQuestionnaireRoute } from '@/app/components/quizletv2/quizlet-config';
import { QuestionnaireAnalyticsHandlers } from '../components/questionnaireV2Ref.page';
import {
  formatAnswerForSubmission,
  formatSkipForSubmission,
  isResponseNotFoundError,
  isValidationError,
  useSubmitAnswer,
  useSubmitQuestionnaireResponse,
} from '@/app/services/questionnaireV2.service';
import { TOAST_DEFAULT_OPTIONS } from '@/constants/toast';
import useAnalytics from '@/hooks/use-analytics';
import { getRoute } from '@/utils';
import { useToast } from '@cmpsr/components';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { OnNext } from '../components/types';
import { useQuestionnaireContext } from '../context/QuestionnaireContext';
import {
  createDefaultValues,
  createQuestionnaireSchema,
  formatValidationErrors,
  isQuestion,
  QuestionnaireFormData,
} from '../schemas/questionnaire-schema';

const isHeight = (item: QuestionnaireAnswer['value'][0]): item is DTOValueFloat => {
  return 'valueFloat' in item && item.valueFloat !== undefined;
};

const isWeight = (item: QuestionnaireAnswer['value'][0]): item is DTOValueInt => {
  return 'valueInt' in item && item.valueInt !== undefined;
};

const isHeightWeight = (item: QuestionnaireAnswer['value']): boolean => {
  // Check if the item contains both height and weight values
  // HACK: this is a workaround for the fact that height and weight are stored in the same array
  return item.length === 2 && item.some(isHeight) && item.some(isWeight);
};

const extractSingleAnswerValue = (item: QuestionnaireAnswer['value'][0]): string | number | boolean | null => {
  if ('valueText' in item) {
    return item.valueText ?? null;
  }
  if ('valueString' in item) {
    return item.valueString ?? null;
  }
  if ('valueInt' in item) {
    return item.valueInt ?? null;
  }
  if ('valueFloat' in item) {
    return item.valueFloat ?? null;
  }
  if ('valueBoolean' in item) {
    return item.valueBoolean ?? null;
  }
  if ('valueDate' in item) {
    return item.valueDate.split('T')?.[0] ?? null;
  }
  if ('valueSkip' in item) {
    // For skipped questions, return null or a specific value if needed
    return null; // or return 'skipped' if you want to indicate skip explicitly
  }
  return null;
};

type ActivityMatrix = Record<string, { daysPerWeek: number; minutesPerDay: number }>;

export const extractAnswerValue = (
  item: QuestionnaireAnswer['value'],
):
  | string[]
  | Record<string, string>
  | Record<string, number>
  | ActivityMatrix
  | null
  | ReturnType<typeof extractSingleAnswerValue> => {
  if (!item || item.length === 0) {
    return null;
  }
  // handle select/multi-select values
  if (item.every((valueItem): valueItem is DTOValueSelect => 'valueString' in valueItem)) {
    return item.map((valueItem) => valueItem.valueString);
  }

  if (item.length === 1) {
    const itemValue = item[0];
    if ('valueExerciseMatrix' in itemValue) {
      return (
        itemValue.valueExerciseMatrix.activities as { activity: string; daysPerWeek: number; minutesPerDay: number }[]
      ).reduce(
        (acc, activity) => {
          acc[activity.activity] = {
            daysPerWeek: activity.daysPerWeek,
            minutesPerDay: activity.minutesPerDay,
          };
          return acc;
        },
        {} as Record<string, { daysPerWeek: number; minutesPerDay: number }>,
      );
    }

    return extractSingleAnswerValue(itemValue);
  } else {
    // Check if this is a name-email answer (2 text values with displayValues)
    if (item.length === 2 && item.every((v) => 'valueText' in v && 'displayValue' in v)) {
      const nameItem = item.find((v) => 'displayValue' in v && v.displayValue === 'name');
      const emailItem = item.find((v) => 'displayValue' in v && v.displayValue === 'email');
      if (nameItem && emailItem && 'valueText' in nameItem && 'valueText' in emailItem) {
        return {
          name: nameItem.valueText || '',
          email: emailItem.valueText || '',
        };
      }
    }

    if (isHeightWeight(item)) {
      // Handle height-weight as a combined object
      const heightWeight: Record<string, number> = {};
      item.forEach((valueItem) => {
        if (isHeight(valueItem)) {
          heightWeight.height = valueItem.valueFloat;
        } else if (isWeight(valueItem)) {
          heightWeight.weight = valueItem.valueInt;
        }
      });
      return heightWeight;
    }
  }
  return null;
};

// Type for the actual return value of getOrCreateResponse function
interface CreateResponseResult {
  questionnaireResponseId: string;
  isNew: boolean;
  status: QuestionnaireResponseStatus;
}

const isFinalInterstitial = (questionSlug: string): boolean => {
  return questionSlug === 'interstitial_outro' || questionSlug.includes('outro');
};

// Helper function to determine if questionnaire should be submitted after current question
const shouldSubmitQuestionnaireAfterQuestion = (nextQuestionSlug: string | null): boolean => {
  return !nextQuestionSlug || isFinalInterstitial(nextQuestionSlug);
};

export function useQuestionnaireForm(
  getOrCreateResponse: () => Promise<CreateResponseResult | null>,
  onResponseNotFound?: () => Promise<void>,
  onComplete?: () => void,
  analyticsHandlers?: QuestionnaireAnalyticsHandlers,
) {
  const { state, dispatch } = useQuestionnaireContext();
  const navigate = useNavigate();
  const { questionSlug } = useParams<{ questionSlug: string }>();
  const analytics = useAnalytics();

  // Determine which route to use based on current path and questionnaire
  const questionRoute = getQuestionnaireRoute(state.questionnaire);

  // Track visited questions and answered questions from backend
  const [visitedQuestions, setVisitedQuestions] = useState<Set<string>>(new Set());
  const [answeredQuestions, setAnsweredQuestions] = useState<Record<string, boolean>>({});

  // Get response ID from context
  const getResponseId = useCallback(() => {
    return state.responseId;
  }, [state.responseId]);

  const questionnaireSchema = useMemo(
    () => (state.questionnaire ? createQuestionnaireSchema(state.questionnaire) : null),
    [state.questionnaire],
  );

  const defaultValues = useMemo(
    () => (state.questionnaire ? createDefaultValues(state.questionnaire) : {}),
    [state.questionnaire],
  );

  const form = useForm<QuestionnaireFormData>({
    resolver: questionnaireSchema ? zodResolver(questionnaireSchema) : undefined,
    mode: 'onSubmit',
    defaultValues,
  });

  const {
    formState: { errors },
    watch,
    trigger,
    getValues,
    resetField,
    reset,
    control,
  } = form;
  const questionsOnly = state.questions.filter(isQuestion);

  // Handle 404 errors using React Query's onError callback
  const handleMutationError = useCallback(
    async (error: any) => {
      if (isResponseNotFoundError(error)) {
        if (!onResponseNotFound) {
          console.error('404 detected but onResponseNotFound callback is not provided!');
          return;
        }

        try {
          await onResponseNotFound();

          // Reset form after successful recovery
          reset(defaultValues);
          setVisitedQuestions(new Set());
          setAnsweredQuestions({});
        } catch (recoveryError) {
          console.error('Failed to recover from 404:', recoveryError);
        }
      }
    },
    [onResponseNotFound, reset, defaultValues, setVisitedQuestions, setAnsweredQuestions],
  );

  const questionnaireId = state.questionnaire?.id;
  const submitAnswerMutation = useSubmitAnswer(questionnaireId, handleMutationError);
  const submitQuestionnaireMutation = useSubmitQuestionnaireResponse(questionnaireId);
  const toast = useToast();

  // Get submittable form data (only answered or visited questions)
  const getSubmittableFormData = useCallback(() => {
    const allFormValues = getValues();
    const submittableData: QuestionnaireFormData = {} as QuestionnaireFormData;

    // Include answered questions from backend
    Object.keys(answeredQuestions).forEach((slug) => {
      if (allFormValues[slug] !== undefined) {
        submittableData[slug] = allFormValues[slug];
      }
    });

    // Include visited questions that have values
    visitedQuestions.forEach((slug) => {
      if (allFormValues[slug] !== undefined && allFormValues[slug] !== defaultValues[slug]) {
        submittableData[slug] = allFormValues[slug];
      }
    });

    return submittableData;
  }, [getValues, answeredQuestions, visitedQuestions, defaultValues]);

  // Load form data from backend answers.
  // Takes questions as a parameter to avoid closing over state.questions,
  // which would cause this callback to get a new reference on every UPDATE_QUESTION_BY_SLUG dispatch.
  const loadFormDataFromAnswers = useCallback(
    (
      responseItems: QuestionnaireAnswer[],
      questions: (QuestionnaireCategoryItemsItemOneOf | QuestionnaireCategoryItemsItemOneOfTwo)[],
    ) => {
      if (!responseItems?.length) {
        return;
      }

      const formData: Partial<QuestionnaireFormData> = {};
      const backendAnswered: Record<string, boolean> = {};

      responseItems.forEach((item) => {
        if (item.slug) {
          backendAnswered[item.slug] = true;
          const value = extractAnswerValue(item.value);
          if (value !== null) {
            // We don't have a good way to detect single select vs multi-select in our current response structure,
            // so we have to compare it to the question type and get the first value if it's a single select.
            const question = questions.find((q) => q.slug === item.slug);
            const isSingleSelect = question && isQuestion(question) && question.questionType === 'select';
            if (Array.isArray(value) && value.length === 1 && isSingleSelect) {
              formData[item.slug] = value[0];
            } else {
              formData[item.slug] = value;
            }
          }
        }
      });

      setAnsweredQuestions(backendAnswered);

      reset({ ...defaultValues, ...formData });
    },
    [defaultValues, reset],
  );

  // Check if user has answered a question
  const isQuestionAnswered = useCallback(
    (questionSlug: string) => {
      const allValues = getValues();
      const fieldValue = allValues[questionSlug];

      const isAnswered =
        fieldValue !== undefined &&
        fieldValue !== null &&
        fieldValue !== '' &&
        !(Array.isArray(fieldValue) && fieldValue.length === 0);

      return Boolean(isAnswered);
    },
    [getValues],
  );

  const onSubmit = async (data: QuestionnaireFormData, isBackgroundSubmission = false) => {
    // Errors propagate — callers decide how to surface them. The global
    // QueryClient mutation retry (client/query.ts) gives transient failures
    // two automatic retries before this throws.
    const finalResult = await submitQuestionnaireMutation.mutateAsync({
      questionnaireId: state.questionnaire!.id,
      responseId: getResponseId()!,
    });

    if (!isBackgroundSubmission) {
      if (Array.isArray(finalResult.missing_questions) && finalResult.missing_questions.length > 0) {
        navigate(getRoute(questionRoute, { questionSlug: finalResult.missing_questions[0] as string }));
        return true;
      }

      if (onComplete) {
        onComplete();
      }
    }
    return true;
  };

  // Helper function to submit answer with error handling
  const submitAnswerWithErrorHandling = async (params: {
    questionnaireId: string;
    responseId: string;
    questionSlug: string;
    data: UpdateQuestionnaireResponseAnswerInput;
  }): Promise<QuestionnaireResponseNextQuestionResponse | null> => {
    try {
      return await submitAnswerMutation.mutateAsync(params);
    } catch (error) {
      if (isResponseNotFoundError(error)) {
        return null;
      }
      if (isValidationError(error)) {
        console.log('is validation error');
        error.response.data.detail.map((err) => form.setError(params.questionSlug, { message: err }));
      }
      throw error;
    }
  };

  // Helper function to navigate to next question
  const navigateToNextQuestion = (nextQuestion: QuestionnaireResponseNextQuestionResponseNextQuestionAnyOf) => {
    const nextQuestionIndex = state.questions.findIndex((q) => q.slug === nextQuestion.slug);
    if (nextQuestionIndex !== -1) {
      dispatch({ type: 'SET_CURRENT_QUESTION_INDEX', payload: nextQuestionIndex });
    }
    dispatch({ type: 'SET_HANDLER_NAVIGATION', payload: true });
    navigate(getRoute(questionRoute, { questionSlug: nextQuestion.slug }));
  };

  // Helper function to validate and submit questionnaire
  const validateAndSubmitQuestionnaire = async () => {
    const requiredQuestions = questionsOnly.filter((q) => q.required);
    const visitedRequiredQuestions = requiredQuestions.filter(
      (q) => visitedQuestions.has(q.slug) || answeredQuestions[q.slug],
    );
    const requiredFieldNames = visitedRequiredQuestions.map((q) => q.slug);

    // Only validate fields that need frontend validation:
    // 1. NOT already answered on the backend (those were validated when submitted)
    // 2. ARE registered with React Hook Form (visited in this session)
    const fieldsToValidate = requiredFieldNames.filter((fieldName) => {
      // If answered on backend, trust that answer (was validated when submitted)
      if (answeredQuestions[fieldName]) {
        return false;
      }
      // Only validate if registered (visited in this session)
      return !!control._fields[fieldName];
    });

    let isFormValid = true;
    if (fieldsToValidate.length > 0) {
      isFormValid = await trigger(fieldsToValidate);
    }

    if (isFormValid) {
      const submittableData = getSubmittableFormData();
      try {
        await onSubmit(submittableData, false);
        return true;
      } catch (error) {
        console.error('Failed to submit questionnaire:', error);
        toast({
          ...TOAST_DEFAULT_OPTIONS,
          title: 'Submission issue',
          description:
            'We had trouble submitting your answers. Your answers are saved \u2014 please try again or refresh.',
          status: 'warning',
        });
        return false;
      }
    } else {
      console.warn('Form has validation errors:', formatValidationErrors(errors));
      return false;
    }
  };

  // Helper function to handle post-submission flow
  const handlePostSubmission = async (
    submitResult: QuestionnaireResponseNextQuestionResponse,
    currentQuestion: QuestionnaireCategoryItemsItemOneOf | QuestionnaireCategoryItemsItemOneOfTwo,
  ) => {
    const shouldSubmit = shouldSubmitQuestionnaireAfterQuestion(submitResult.nextQuestion?.slug || null);

    if (shouldSubmit && !submitQuestionnaireMutation.isSuccess) {
      // Check if the NEXT question (where we're navigating to) is the outro
      if (submitResult.nextQuestion && isFinalInterstitial(submitResult.nextQuestion.slug)) {
        // Fire the submit in parallel with navigating to the outro — the UX
        // shouldn't block on the network — but track the promise so we can
        // surface a toast if it ultimately fails. The backend also
        // auto-completes on the terminal answer write and has an hourly
        // recovery sweeper, so a failure here is a UX issue (user doesn't
        // know their submission was recorded) rather than a data issue.
        onSubmit({}, true).catch((error) => {
          console.error('Background questionnaire submission failed:', error);
          toast({
            ...TOAST_DEFAULT_OPTIONS,
            title: 'Submission issue',
            description:
              'We had trouble confirming your submission. Your answers are saved — please refresh if the dashboard doesn\u2019t update.',
            status: 'warning',
          });
        });
      } else {
        // For all other cases, validate before submitting
        const submitted = await validateAndSubmitQuestionnaire();
        if (!submitted) return false;
      }
    }

    if (submitResult.nextQuestion) {
      navigateToNextQuestion(submitResult.nextQuestion);
    } else {
      // No more questions left - need to submit the questionnaire
      // If we're currently on the outro (final interstitial), navigate to dashboard
      if (
        'questionnaireItemKind' in currentQuestion &&
        currentQuestion.questionnaireItemKind === 'interstitial' &&
        isFinalInterstitial(currentQuestion.slug)
      ) {
        // For outro interstitials, submit the questionnaire first if not already done
        if (!submitQuestionnaireMutation.isSuccess && !submitQuestionnaireMutation.isPending) {
          const submitted = await validateAndSubmitQuestionnaire();
          if (!submitted) return false;
        }

        if (onComplete) {
          onComplete();
        }
      } else if (!submitQuestionnaireMutation.isSuccess && !submitQuestionnaireMutation.isPending) {
        // For non-outro questions, submit first then navigate
        const submitted = await validateAndSubmitQuestionnaire();
        if (!submitted) return false;

        if (onComplete) {
          // Questionnaire navigation in
          onComplete();
        }
      }
    }
    return true;
  };

  // Track answer submission to Amplitude
  const trackAnswerSubmission = (
    currentQuestion: QuestionnaireCategoryItemsItemOneOf | QuestionnaireCategoryItemsItemOneOfTwo,
    answerInput: UpdateQuestionnaireResponseAnswerInput,
  ) => {
    // Get the current category/section name
    const currentCategory = state.questionnaire?.categories.find((cat) =>
      cat.items.some((item) => item.slug === currentQuestion.slug),
    );
    const sectionName = currentCategory?.name || 'Unknown Section';

    // Stringify the serialized answer for Amplitude
    const answerValue = JSON.stringify(answerInput.value);
    const questionType = isQuestion(currentQuestion) ? currentQuestion.questionType : currentQuestion.type;

    // Track via external analytics handlers (e.g. Trial Prequel) or fall back to generic event
    if (analyticsHandlers?.onAnswerSubmitted) {
      analyticsHandlers.onAnswerSubmitted(currentQuestion.slug, answerValue);
    } else {
      analytics.callAnswerSubmitted(
        sectionName,
        currentQuestion.slug,
        answerValue,
        state.questionnaire?.slug || 'unknown',
        currentQuestion.slug,
        questionType,
      );
    }
  };

  const handleNext: OnNext = async ({ skipQuestion, syntheticAnswer, metadata }) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_NAVIGATION_DIRECTION', payload: 'forward' });

    // Predict next question and show skeleton for instant visual feedback
    // Use backend hint if available from previous API response
    const hint = state.nextSurveyItemTypeHint;
    const currentIndex = questionSlug
      ? state.questions.findIndex((q) => q.slug === questionSlug)
      : state.currentQuestionIndex;
    const predictedNextQuestion = state.questions[currentIndex + 1];

    if (predictedNextQuestion) {
      let questionType: string;
      let optionCount: number | undefined;

      // If we have a hint from backend, use it to determine skeleton type
      if (hint === NextSurveyItemTypeHint.alwaysInterstitial) {
        // Backend says it's definitely an interstitial - use generic interstitial type
        questionType = 'image-interstitial';
      } else if (hint === NextSurveyItemTypeHint.alwaysQuestion) {
        // Backend says it's definitely a question - still use local prediction for specifics
        questionType = isQuestion(predictedNextQuestion) ? predictedNextQuestion.questionType : 'select';
        optionCount =
          isQuestion(predictedNextQuestion) && 'options' in predictedNextQuestion && predictedNextQuestion.options
            ? predictedNextQuestion.options.length
            : undefined;
      } else {
        // No hint or dependsOnResponse - fall back to local prediction
        questionType = isQuestion(predictedNextQuestion) ? predictedNextQuestion.questionType : predictedNextQuestion.type;
        optionCount =
          isQuestion(predictedNextQuestion) && 'options' in predictedNextQuestion && predictedNextQuestion.options
            ? predictedNextQuestion.options.length
            : undefined;
      }

      dispatch({
        type: 'SET_SKELETON',
        payload: { questionType, optionCount },
      });
    }

    // Yield to the browser so React can flush the skeleton state update and paint
    // before the validation + API call occupies the main thread.
    await new Promise((resolve) => setTimeout(resolve, 0));

    try {
      // Get current question from URL slug instead of index to avoid sync issues
      const currentQuestion = questionSlug
        ? state.questions.find((q) => q.slug === questionSlug)
        : state.questions[state.currentQuestionIndex];

      if (!currentQuestion) {
        console.error(
          'Current question not found for slug:',
          questionSlug,
          'Available questions:',
          state.questions.map((q) => q.slug),
        );
        return;
      }

      // Mark as visited
      setVisitedQuestions((prev) => new Set([...prev, currentQuestion.slug]));

      // Prepare answer data
      let answerInput: UpdateQuestionnaireResponseAnswerInput;

      // Handle synthetic answers from interceptors
      if (syntheticAnswer !== undefined) {
        // Synthetic answer bypasses form validation and uses provided value directly
        answerInput = formatAnswerForSubmission({
          value: syntheticAnswer,
          questionType: isQuestion(currentQuestion) ? currentQuestion.questionType : 'select',
          units: null,
        })!;
      }
      // Handle skips and interstitials
      else if (
        ((isQuestion(currentQuestion) && !currentQuestion.required) ||
          currentQuestion.questionnaireItemKind === 'interstitial') &&
        skipQuestion
      ) {
        answerInput = formatSkipForSubmission(currentQuestion.questionnaireItemKind);
      } else if (isQuestion(currentQuestion)) {
        // Handle regular question answers (user-submitted)
        const currentQuestionValue = getValues(currentQuestion.slug);

        if (currentQuestionValue === undefined) {
          // failsafe to set default
          // must not depend on this. race condition can occur if user clicks fast
          // ideally, the UI should prevent this from happening, by setting defaultValue in each input component
          console.warn('Value undefined, setting to default:', currentQuestion.slug, defaultValues);
          form.setValue(currentQuestion.slug, defaultValues[currentQuestion.slug]);
        }

        // Always validate when user clicks "Next" (skips happen explicitly via "Skip" button)
        const isValid = await trigger(currentQuestion.slug);
        // Get the updated error state after validation
        const { error } = form.getFieldState(currentQuestion.slug);
        if (!isValid) {
          console.warn('Validation failed:', currentQuestion.slug, error, errors);
          return;
        }

        if (currentQuestionValue === undefined || currentQuestionValue === null) {
          throw new Error(`Invariant: currentQuestionValue is undefined or null for slug: ${currentQuestion.slug}`);
        }

        const units = currentQuestion.questionType === 'number' && currentQuestion.units ? currentQuestion.units : null;
        const formattedAnswer = formatAnswerForSubmission({
          value: currentQuestionValue,
          questionType: currentQuestion.questionType,
          units,
        });

        if (formattedAnswer === null) {
          console.error(
            'Failed to format answer for submission. This should not happen as validation should catch this.',
          );
          return;
        }

        answerInput = formattedAnswer;
      } else {
        throw new Error(`Invariant: unknown questionnaireItemKind: ${currentQuestion.questionnaireItemKind}`);
      }

      // Submit answer
      const submitResult = await submitAnswerWithErrorHandling({
        questionnaireId: state.questionnaire!.id,
        responseId: getResponseId()!,
        questionSlug: currentQuestion.slug,
        data: answerInput,
      });

      if (!submitResult) return; // 404 error handled

      // Update the cached question with personalized content from backend response
      if (submitResult.nextQuestion) {
        dispatch({
          type: 'UPDATE_QUESTION_BY_SLUG',
          payload: {
            slug: submitResult.nextQuestion.slug,
            question: submitResult.nextQuestion as QuestionnaireCategoryItemsItemOneOf | QuestionnaireCategoryItemsItemOneOfTwo,
          },
        });
      }

      // Store the hint from backend for the NEXT transition's skeleton prediction
      dispatch({
        type: 'SET_NEXT_SURVEY_ITEM_TYPE_HINT',
        payload: submitResult.nextSurveyItemTypeHint ?? null,
      });

      // Track the answer submission to Amplitude
      trackAnswerSubmission(currentQuestion, answerInput);

      // Handle post-submission (questionnaire submission check + navigation)
      await handlePostSubmission(submitResult, currentQuestion);
    } catch (error) {
      console.error('Error in handleNext:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'CLEAR_SKELETON' });
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return {
    form,
    errors,
    watch,
    trigger,
    getValues,
    resetField,
    handleNext,
    handleFormSubmit,
    FormProvider,
    isQuestionAnswered,
    loadFormDataFromAnswers,
    getSubmittableFormData,
  };
}
